import React, { useState, useEffect } from 'react';
import { useWorkspacesContext } from '../contexts/WorkspacesContext';
import { Activity, Play, Server, Globe, Loader2, AlertCircle, Save, Copy, Inbox, Flame } from 'lucide-react';
import api from '../api/axios';
import { decryptFromStorage } from '../secure/secureStorage';
import { saveProdVitalsRecord, getProdVitalsRecords } from '../services/LocalStorage';
import { useAuth } from '../contexts/AuthContext';

const saveBulkServerStatus = async (statuses) => {
  try {
    const response = await api.post('/monitoring/log-bulk', statuses);
    return response.data;
  } catch (error) {
    console.error("Monitoring Error:", error);
    throw error;
  }
};

const ProdVitalsPage = () => {
  const { user } = useAuth();
  const { workspaces, activeWorkspaceId, setActiveWorkspaceId, activeWorkspace } = useWorkspacesContext();
  const currentWorkspaceIdRef = React.useRef(activeWorkspaceId);

  useEffect(() => {
    currentWorkspaceIdRef.current = activeWorkspaceId;
  }, [activeWorkspaceId]);

  const canUpdate = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_MANAGER' || user?.role === 'ROLE_TEAM_LEADER';

  const getTodayStart = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().slice(0, 16);
  };

  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(100);
  const [sinceDays, setSinceDays] = useState(0);
  const [filterAfterDate, setFilterAfterDate] = useState(getTodayStart());
  const [draftAfterDate, setDraftAfterDate] = useState(getTodayStart());
  const [serverList, setServerList] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [subjects, setSubjects] = useState([]); // Unique subjects found
  const [matrixData, setMatrixData] = useState([]); // Row data
  const [subjectScores, setSubjectScores] = useState([]);

  // --- PERSISTENCE LOGIC ---
  const saveToLocalStorage = (workspaceId, data) => {
    if (!workspaceId) return;
    const key = `prod_vitals_settings_${workspaceId}`;
    try {
      const saved = localStorage.getItem(key) || "{}";
      const current = JSON.parse(saved);
      const updated = { ...current, ...data };
      localStorage.setItem(key, JSON.stringify(updated));
    } catch (e) {
      console.error("Failed to save to localStorage", e);
    }
  };

  // Load state on workspace switch
  useEffect(() => {
    if (!activeWorkspaceId) return;

    const key = `prod_vitals_settings_${activeWorkspaceId}`;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const settings = JSON.parse(saved);
        setStartIndex(settings.startIndex ?? 0);
        setEndIndex(settings.endIndex ?? 100);
        setSinceDays(settings.sinceDays ?? 0);
        setFilterAfterDate(settings.filterAfterDate ?? '');
        setDraftAfterDate(settings.filterAfterDate ?? '');
        setServerList(settings.serverList ?? "");
        setSubjects(settings.subjects ?? []);
        setMatrixData(settings.matrixData ?? []);
        setSubjectScores(settings.subjectScores ?? []);
      } else {
        const todayStart = getTodayStart();
        setStartIndex(0);
        setEndIndex(100);
        setSinceDays(0);
        setFilterAfterDate(todayStart);
        setDraftAfterDate(todayStart);
        setServerList("");
        setSubjects([]);
        setMatrixData([]);
        setSubjectScores([]);
      }
    } catch (e) {
      console.error("Failed to load settings", e);
    }
    setError(null);
  }, [activeWorkspaceId]);

  // Auto-save input settings (not results) to avoid state noise
  useEffect(() => {
    if (!activeWorkspaceId) return;
    saveToLocalStorage(activeWorkspaceId, { startIndex, endIndex, serverList, sinceDays, filterAfterDate });
  }, [startIndex, endIndex, serverList, activeWorkspaceId, sinceDays, filterAfterDate]);

  const handleAnalyse = async () => {
    if (!activeWorkspace) {
        setError("Please select a mailbox first.");
        return;
    }
    if (!serverList.trim()) {
        setError("Please provide server:ip list.");
        return;
    }

    setIsLoading(true);
    setError(null);

    const passphrase = sessionStorage.getItem("yassu_pf") || "";
    const initialWorkspaceId = activeWorkspaceId;
    
    try {
        const realPassword = await decryptFromStorage(activeWorkspace.password, passphrase);

        const fetchMails = async (folder) => {
            const body = {
                username: activeWorkspace.email,
                appPassword: realPassword,
                folder: folder,
                sinceDays: Number(sinceDays) || 0,
                receivedAfter: filterAfterDate || undefined,
                startIndex: Number(startIndex) || 0,
                endIndex: Number(endIndex) || 100,
            };
            const res = await api.post("/mail/search", body);
            return Array.isArray(res.data) ? res.data.map(m => ({ ...m, foundIn: folder })) : [];
        };

        const [inboxMails, spamMails] = await Promise.all([
            fetchMails("INBOX"),
            fetchMails("SPAM")
        ]);

        if (currentWorkspaceIdRef.current !== initialWorkspaceId) return;

        const allMails = [...inboxMails, ...spamMails];

        // ... (Analyse logic remains the same)
        const allUniqueSubjects = [...new Set(allMails.map(m => m.subject))].filter(Boolean);
        const lines = serverList.split('\n').filter(line => line.trim().includes(':'));
        const infraPairs = lines.map(line => {
            const [server, ip] = line.split(':').map(s => s.trim());
            return { server, ip };
        }).filter(p => p.server && p.ip);

        const filteredSubjects = allUniqueSubjects.filter(sub => 
            infraPairs.some(pair => 
                allMails.some(m => 
                    (m.senderIp === pair.ip || m.originIp === pair.ip) && m.subject === sub
                )
            )
        );

        const matrix = infraPairs.map(pair => {
            const rowSubjects = {};
            let inboxCount = 0;
            let totalHits = 0;
            filteredSubjects.forEach(sub => {
                const match = allMails.find(m => (m.senderIp === pair.ip || m.originIp === pair.ip) && m.subject === sub);
                if (match) {
                    rowSubjects[sub] = match.foundIn;
                    totalHits++;
                    if (match.foundIn === 'INBOX') inboxCount++;
                } else {
                    rowSubjects[sub] = 'NOT_DELIVERED';
                }
            });
            const score = totalHits > 0 ? Math.round((inboxCount / totalHits) * 100) : 0;
            return { ...pair, subjects: rowSubjects, score, totalHits };
        });

        const calculatedSubjectScores = filteredSubjects.map(sub => {
            let inboxCount = 0;
            let spamCount = 0;
            let deferredCount = 0;
            matrix.forEach(row => { 
                const status = row.subjects[sub];
                if (status === 'INBOX') inboxCount++;
                else if (status === 'SPAM') spamCount++;
                else deferredCount++;
            });
            const totalDetected = inboxCount + spamCount;
            const score = totalDetected > 0 ? Math.round((inboxCount / totalDetected) * 100) : 0;
            return { subject: sub, score, inboxCount, spamCount, deferredCount, totalDetected };
        });

        // UPDATE STATE
        setSubjects(filteredSubjects);
        setMatrixData(matrix);
        setSubjectScores(calculatedSubjectScores);

        // PERSIST IMMEDIATELY
        saveToLocalStorage(initialWorkspaceId, {
          subjects: filteredSubjects,
          matrixData: matrix,
          subjectScores: calculatedSubjectScores
        });

    } catch (err) {
        if (currentWorkspaceIdRef.current === initialWorkspaceId) {
            console.error("Analysis failed:", err);
            setError(err.message === "Decryption failed" ? "Incorrect Passphrase." : "Failed to fetch or process mails.");
        }
    } finally {
        if (currentWorkspaceIdRef.current === initialWorkspaceId) {
            setIsLoading(false);
        }
    }
  };

  const handleUpdateFile = async () => {
    if (!matrixData.length) {
        setError("No analysis results to save. Please run analysis first.");
        return;
    }
    setIsSaving(true);
    setError(null);

    const statusesToSave = matrixData.map((row) => {
      let aggregatedStatus = 'NOT_DELIVERED'; 

      if (row.subjects) {
        const subjectStatuses = Object.values(row.subjects);
        
        if (subjectStatuses.includes('INBOX')) {
          aggregatedStatus = 'INBOX';
        } else if (subjectStatuses.includes('SPAM')) {
          aggregatedStatus = 'SPAM';
        } else if (subjectStatuses.includes('NOT_DELIVERED')) {
          aggregatedStatus = 'NOT_DELIVERED';
        }
      }

      return {
        serverName: row.server,
        ipAddress: row.ip,
        status: aggregatedStatus,
        checkTime: new Date().toISOString()
      };
    });

    try {
        const response = await saveBulkServerStatus(statusesToSave);
        if (response && Array.isArray(response)) { 
            console.log("Bulk server statuses saved successfully:", response);
            alert("All analysis results saved to database successfully!");
        } else {
            console.error("Failed to save bulk server statuses:", response);
            alert("Failed to save all analysis results to database. Check console for details.");
        }
    } catch (e) {
        console.error("Error saving bulk server statuses:", e);
        alert("Error saving all analysis results to database. Check console for details.");
    }


    try {
        const record = {
            timestamp: new Date().toISOString(),
            mailboxId: activeWorkspace.id,
            mailboxEmail: activeWorkspace.email,
            startIndex,
            endIndex,
            serverListRaw: serverList,
            subjects,
            matrixData,
            subjectScores,
        };
        saveProdVitalsRecord(record);
        console.log("Analysis results also saved to local storage.");
    } catch (e) {
        console.error("Failed to save prod vitals record to local storage:", e);
    }
    
    setIsSaving(false);
  };


  const handleDownloadData = () => {
    const allRecordsByDateMailbox = getProdVitalsRecords();
    
    const allRecords = Object.values(allRecordsByDateMailbox).flatMap(mailboxes => 
        Object.values(mailboxes).flatMap(recordsArray => recordsArray)
    );

    if (allRecords.length === 0) {
      alert("No records to download.");
      return;
    }

    let csvString = "";

    csvString += "--- Subject Summary Across All Records ---\n";
    const aggregatedSubjects = {}; 

    allRecords.forEach(record => {
        if (record.subjectScores && record.subjectScores.length > 0) {
            record.subjectScores.forEach(s => {
                const subjectKey = s.subject || 'UNKNOWN_SUBJECT';
                if (!aggregatedSubjects[subjectKey]) {
                    aggregatedSubjects[subjectKey] = {
                        inbox: 0,
                        spam: 0,
                        notDelivered: 0,
                        total: 0
                    };
                }
                const currentSubject = aggregatedSubjects[subjectKey];
                currentSubject.inbox += s.inboxCount || 0;
                currentSubject.spam += s.spamCount || 0;
                currentSubject.notDelivered += s.deferredCount || 0;
                currentSubject.total += (s.totalDetected || 0) + (s.deferredCount || 0);
            });
        }
    });

    if (Object.keys(aggregatedSubjects).length > 0) {
        csvString += "Subject,Total Inbox,Total Spam,Total Not Delivered,Overall Delivery Score (%)\n";
        Object.entries(aggregatedSubjects).forEach(([subject, data]) => {
            const overallScore = data.total > 0 ? Math.round((data.inbox / (data.inbox + data.spam)) * 100) : 0;
            csvString += `"${(subject || '').replace(/"/g, '""')}",${data.inbox},${data.spam},${data.notDelivered},${overallScore}%\n`;
        });
        csvString += "\n\n";
    } else {
        csvString += "No Subject Summary Data.\n\n";
    }

    csvString += "--- Flattened Historical Delivery Status (Per Mailbox Analysis) ---\n";
    const flattenedDataHeaders = ["Date", "Time", "Mailbox", "Mailbox ID", "Server", "IP", "Subject", "Status", "IP Rep Score (%)", "Start Index", "End Index"];
    csvString += flattenedDataHeaders.map(h => `"${h}"`).join(",") + "\n";

    allRecords.forEach(record => {
      const recordDate = new Date(record.timestamp);
      const dateStr = recordDate.toLocaleDateString();
      const timeStr = recordDate.toLocaleTimeString();

      if (record.matrixData && record.matrixData.length > 0 && record.subjects && record.subjects.length > 0) {
        record.matrixData.forEach(row => {
          (record.subjects || []).forEach(sub => { 
            const status = row.subjects[sub] || 'NOT_DELIVERED';
            const rowValues = [
              `"${dateStr}"`,
              `"${timeStr}"`,
              `"${(record.mailboxEmail || '').replace(/"/g, '""')}"`,
              `"${(record.mailboxId || '')}"`,
              `"${(row.server || '').replace(/"/g, '""')}"`,
              `"${(row.ip || '').replace(/"/g, '""')}"`,
              `"${(sub || '').replace(/"/g, '""')}"`,
              `"${(status || '').replace(/"/g, '""')}"`,
              `${row.score || 0}%`,
              record.startIndex || '',
              record.endIndex || '',
            ];
            csvString += rowValues.join(",") + "\n";
          });
        });
      } else {
        csvString += `"${dateStr}","${timeStr}","${(record.mailboxEmail || '').replace(/"/g, '""')}","${(record.mailboxId || '')}","No Matrix Data or Subjects",,,,,,\n`;
      }
    });
    csvString += "\n\n";

    const filename = `checkMyHits_prod_vitals_report_${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  return (
    <div className="container-fluid p-4 bg-light min-vh-100">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
            <div className="bg-primary bg-opacity-10 p-3 rounded-3">
            <Activity size={24} className="text-primary" />
            </div>
            <div>
            <h4 className="fw-bold m-0">Prod Vitals</h4>
            <p className="text-muted small m-0">Dynamic delivery matrix and IP reputation analysis</p>
            </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-4 border-0 shadow-sm">
            <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Subject Summary Area */}
      {subjectScores.length > 0 && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 border-0">
            <h6 className="fw-bold m-0 d-flex align-items-center gap-2">
              <Activity size={18} className="text-primary" />
              Subject Summary
            </h6>
          </div>
          <div className="card-body d-flex flex-wrap gap-3">
            {subjectScores.map((s, idx) => (
              <div key={idx} className="border rounded-3 p-3 flex-grow-1" style={{ minWidth: '200px', maxWidth: '300px' }}>
                <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                  <p className="fw-bold text-truncate mb-0" title={s.subject} style={{ maxWidth: '75%' }}>{s.subject}</p>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <button 
                      className="btn btn-sm btn-light p-1 border-0 shadow-none bg-transparent" 
                      title="Copy Subject" 
                      onClick={() => navigator.clipboard.writeText(s.subject)}
                    >
                      <Copy size={14} className="text-muted hover-primary" />
                    </button>
                    <button 
                      className="btn btn-sm btn-light p-1 border-0 shadow-none bg-transparent" 
                      title="Copy Inbox IPs" 
                      onClick={() => {
                        const ips = matrixData
                          .filter(row => row.subjects && row.subjects[s.subject] === 'INBOX')
                          .map(row => row.ip)
                          .join('\n');
                        if (ips) {
                          navigator.clipboard.writeText(ips);
                        } else {
                          alert("No INBOX IPs found for this subject.");
                        }
                      }}
                    >
                      <Inbox size={14} className="text-success hover-primary" />
                    </button>
                    <button 
                      className="btn btn-sm btn-light p-1 border-0 shadow-none bg-transparent" 
                      title="Copy Spam IPs" 
                      onClick={() => {
                        const ips = matrixData
                          .filter(row => row.subjects && row.subjects[s.subject] === 'SPAM')
                          .map(row => row.ip)
                          .join('\n');
                        if (ips) {
                          navigator.clipboard.writeText(ips);
                        } else {
                          alert("No SPAM IPs found for this subject.");
                        }
                      }}
                    >
                      <Flame size={14} className="text-danger hover-primary" />
                    </button>
                    <button 
                      className="btn btn-sm btn-light p-1 border-0 shadow-none bg-transparent" 
                      title="Copy All Delivered IPs" 
                      onClick={() => {
                        const ips = matrixData
                          .filter(row => row.subjects && row.subjects[s.subject] && row.subjects[s.subject] !== 'NOT_DELIVERED')
                          .map(row => row.ip)
                          .join('\n');
                        if (ips) {
                          navigator.clipboard.writeText(ips);
                        } else {
                          alert("No delivered IPs found for this subject.");
                        }
                      }}
                    >
                      <Server size={14} className="text-primary hover-primary" />
                    </button>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="small text-muted">Overall Score:</span>
                  <span className="fw-bold" style={{ color: s.score > 70 ? 'var(--success)' : s.score > 30 ? 'var(--warning)' : 'var(--danger)' }}>{s.score}%</span>
                </div>
                <div className="d-flex align-items-center justify-content-between small text-muted">
                  <span style={{ color: s.inboxCount > 0 ? 'var(--success)' : 'inherit' }}>Inbox: {s.inboxCount}</span>
                  <span style={{ color: s.spamCount > 0 ? 'var(--danger)' : 'inherit' }}>Spam: {s.spamCount}</span>
                  <span style={{ color: s.deferredCount > 0 ? 'var(--warning)' : 'inherit' }}>Not Delivered: {s.deferredCount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control Panel */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-12 mb-2">
              <label className="form-label small fw-bold text-uppercase text-muted">Infrastructure Data (server:ip per line)</label>
              <textarea 
                className="form-control border-0 bg-light shadow-none" 
                rows="4"
                placeholder="servername1:1.1.1.1&#10;servername2:2.2.2.2"
                value={serverList}
                onChange={(e) => setServerList(e.target.value)}
                style={{ fontSize: '13px', fontFamily: 'monospace' }}
              />
            </div>
            
            <div className="col-md-4">
              <label className="form-label small fw-bold text-uppercase text-muted">Select Mailbox</label>
              <select 
                className="form-select border-0 bg-light shadow-none" 
                value={activeWorkspaceId || ''} 
                onChange={(e) => setActiveWorkspaceId(e.target.value)}
              >
                <option value="" disabled>Choose a workspace...</option>
                {workspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>
                    {ws.accountName || ws.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-uppercase text-muted">Start Index</label>
              <input 
                type="number" 
                className="form-control border-0 bg-light shadow-none" 
                value={startIndex} 
                onChange={(e) => setStartIndex(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-uppercase text-muted">End Index</label>
              <input 
                type="number" 
                className="form-control border-0 bg-light shadow-none" 
                value={endIndex} 
                onChange={(e) => setEndIndex(e.target.value)}
              />
            </div>
            <div className="col-md-1">
              <label className="form-label small fw-bold text-uppercase text-muted" title="Since Days">Since</label>
              <input 
                type="number" 
                className="form-control border-0 bg-light shadow-none" 
                value={sinceDays} 
                onChange={(e) => setSinceDays(e.target.value)}
                min="0"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small fw-bold text-uppercase text-muted">Received After</label>
              <div className="input-group">
                <input 
                  type="datetime-local" 
                  className="form-control border-0 bg-light shadow-none p-1" 
                  style={{ fontSize: '11px' }}
                  value={draftAfterDate} 
                  onChange={(e) => setDraftAfterDate(e.target.value)} 
                />
                <button 
                  className="btn btn-primary px-2" 
                  style={{ fontSize: '10px' }} 
                  onClick={() => setFilterAfterDate(draftAfterDate)}
                >
                  Apply
                </button>
                {filterAfterDate && (
                  <button 
                    className="btn btn-light border-0 px-2" 
                    onClick={() => {
                      setFilterAfterDate('');
                      setDraftAfterDate('');
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-12 d-flex justify-content-end gap-2 mt-2">
              <button 
                className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm"
                onClick={handleAnalyse}
                disabled={isLoading || !activeWorkspaceId || !serverList.trim()}
              >
                {isLoading ? <Loader2 size={18} className="spin-animation" /> : <Play size={18} />}
                Analyse Prod
              </button>
              <button 
                className="btn btn-success d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm"
                onClick={handleUpdateFile}
                disabled={isSaving || !matrixData.length || !canUpdate}
                title={!canUpdate ? "You do not have permission to update data." : "Update analysis results to database"}
              >
                {isSaving ? <Loader2 size={18} className="spin-animation" /> : <Save size={18} />}
                Update Data
              </button>
              <button
                className="btn btn-info d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm"
                onClick={handleDownloadData}
                disabled={Object.keys(getProdVitalsRecords()).length === 0}
                title="Download all saved records"
              >
                <Save size={18} /> Download Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table - Matrix */}
      <div className="card border-0 shadow-sm overflow-hidden">
        <div className="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
          <h6 className="fw-bold m-0 d-flex align-items-center gap-2">
            <Server size={18} className="text-primary" />
            Delivery Matrix Results
          </h6>
          {matrixData.length > 0 && <span className="badge bg-light text-dark border">Found {subjects.length} Subjects</span>}
        </div>
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0 text-center" style={{ minWidth: '100%' }}>
            <thead className="bg-light">
              <tr>
                <th className="px-3 py-2 text-uppercase small text-muted text-start" style={{ minWidth: '120px' }}>Server</th>
                <th className="px-3 py-2 text-uppercase small text-muted text-start" style={{ minWidth: '140px' }}>IP</th>
                {subjects.map((sub, i) => (
                    <th key={i} className="px-2 py-2 small text-muted text-truncate" style={{ maxWidth: '120px' }} title={sub}>
                        {sub}
                    </th>
                ))}
                <th className="px-3 py-2 text-uppercase small text-muted" style={{ minWidth: '100px' }}>Rep Score</th>
              </tr>
            </thead>
            <tbody>
              {matrixData.length > 0 ? (
                matrixData.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-start fw-bold small">{row.server}</td>
                    <td className="px-3 py-2 text-start small font-monospace">{row.ip}</td>
                    {subjects.map((sub, i) => {
                        const status = row.subjects[sub];
                        return (
                            <td key={i} className="px-2 py-2"
                                style={{
                                    backgroundColor: status === 'INBOX' ? 'var(--primary-light)' : 
                                                     status === 'SPAM' ? 'var(--danger-light)' : 
                                                     'var(--warning-light)', 
                                    color: status === 'INBOX' ? 'var(--primary)' : 
                                           status === 'SPAM' ? 'var(--danger)' : 
                                           'var(--warning)',
                                    fontWeight: 'bold',
                                    fontSize: '11px',
                                    verticalAlign: 'middle',
                                }}
                            >
                                {status === 'INBOX' ? 'INBOX' : 
                                 status === 'SPAM' ? 'SPAM' : 
                                 'NOT_DELIVERED'}
                            </td>
                        );
                    })}
                    <td className="px-3 py-2">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <div className="progress flex-grow-1" style={{ height: '6px', width: '40px' }}>
                            <div 
                                className={`progress-bar ${row.score > 70 ? 'bg-success' : row.score > 30 ? 'bg-warning' : 'bg-danger'}`} 
                                style={{ width: `${row.score}%` }}
                            ></div>
                        </div>
                        <span className="small fw-bold">{row.score}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={subjects.length + 3} className="text-center py-5 text-muted">
                    {isLoading ? (
                      <div className="d-flex flex-column align-items-center gap-2">
                        <Loader2 size={32} className="spin-animation text-primary" />
                        <span>Scanning folders and matching headers...</span>
                      </div>
                    ) : (
                      <div className="opacity-50">
                        <Globe size={40} className="mb-3" />
                        <p>No analysis results. Select a mailbox, add your server list, and click Analyse Prod.</p>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProdVitalsPage;